import React from 'react'
import { Box, CheckboxLabel, CheckboxInput, Input, RadioInput, RadioLabel, Text, Textarea } from '@maui/elements'
import { RequiredIndicator, TimePicker, DropDownSelect, InfoTooltip } from '../common'
import { timeZones, deliveryMethodList, daysMap } from '../../utils/helpers'
import { useFormContext } from './formUtils'

export default function AnalystInvestigation2() {
  const { state, dispatch } = useFormContext()
  const handleTimePicker = (value) => dispatch({ type: 'updateTimePicker', payload: value })

  return (
    <>
      <Box display='flex' flexDirection='row' alignItems='center' mb='spaceGapXLarge'>
        <Text variant='heading3'>Contact & Availability</Text>
        <Box borderTop='0.5px solid' borderColor='colorActionPrimary' width='60%' ml='10px'></Box>
      </Box>

      <Box mb="spaceGapXLarge">
        <Text variant="heading5" mb="spaceGapXLarge">
          How Should We Contact You? <RequiredIndicator />
        </Text>
        <Box gridGap="8px" mb="spaceGapLarge" display="flex" flexDirection="column" >
          <Box display="flex" alignItems="center" height='30px'>
            <RadioLabel mb='0' padding='0'>
              <RadioInput
                mr='spaceGapSmall'
                name="contact_preference"
                value="email"
                checked={state.contact_preference.value === 'email'}
                onChange={(e) => dispatch({ type: 'updateField', payload: e.target })}
                id="contact-email"
              />
            </RadioLabel>
            <Text ml="spaceGapSmall" variant='heading6' as="label" htmlFor="contact-email">
              Email
            </Text>
          </Box>
          <Box display="flex" alignItems="center" height='30px'>
            <RadioLabel mb='0' padding='0'>
              <RadioInput
                mr='spaceGapSmall'
                name="contact_preference"
                value="phone"
                onChange={(e) => dispatch({ type: 'updateField', payload: e.target })}
                checked={state.contact_preference.value === 'phone'}
                id="contact-phone"
              />
            </RadioLabel>
            <Text ml="spaceGapSmall" variant='heading6' as="label" htmlFor="contact-phone">
              Phone
            </Text>
            {/* phone regex from here https://stackoverflow.com/a/16699507/5569369 */}
            <Input
              ml="spaceGapLarge"
              name="contactPhone"
              placeholder="Enter your phone number"
              type="tel"
              pattern="^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$"
              value={state.contactPhone.value}
              onChange={(e) => dispatch({ type: 'updateField', payload: e.target })}
              size='medium'
              width='38%'
              disabled={state.contact_preference.value !== 'phone'}
              required={state.contact_preference.value === 'phone'}
            />

          </Box>
        </Box>
      </Box>

      <Box marginBottom="spaceGapLarge">
        <Box display="flex" mb="spaceGapMedium">
          <Text variant="heading5" marginBottom="spaceGapMedium">
            What is your availability?
          </Text>
          <InfoTooltip
            ml="spaceGapMedium"
            tooltip='This allows us to contact you at a time that is most convenient for you.'
          />
        </Box>
        <Text as="label" fontSize="10px" fontWeight="600" display="block" marginBottom="spaceGapSmall" htmlFor='timeZoneSelect'>
          Time Zone
        </Text>
        <DropDownSelect id='timeZoneSelect' name='availability_timezone' menuLabel="Time Zones" list={timeZones.map(({ label }) => label)} selectedItem={state.availability_timezone.value} onClick={(item) => dispatch({ type: 'updateField', payload: { name: 'availability_timezone', value: item } })}/>
      </Box>
      <Box marginBottom="spaceGapLarge">
        <Text as="label" fontSize="10px" fontWeight="600" display="block" marginBottom="spaceGapMedium">
          Select Which Days You&apos;re Available To Discuss Your Request
        </Text>
        <Box display="flex">
          {Object.keys(daysMap).map((day, index) => {
            return (
              <Box key={index} mr="spaceGapXLarge" display="flex" alignItems="center">
                <CheckboxLabel mr='spaceGapSmall'>
                  <CheckboxInput
                    name="availability_days"
                    id={`day-${daysMap[day]}`}
                    onChange={() => dispatch({ type: 'updateDaysAvailable', payload: day })}
                    value={daysMap[day]}
                    checked={state.availability_days[day]}
                  />
                </CheckboxLabel>
                <Text variant='heading6' as="label" htmlFor={`day-${daysMap[day]}`} aria-label={day}>
                  {daysMap[day]}
                </Text>
              </Box>
            )
          })}
        </Box>
      </Box>
      <TimePicker onChange={handleTimePicker} stateCheckedObject={state.availability_hours}/>

      <Box display='flex' flexDirection='row' alignItems='center' mb='spaceGapXLarge'>
        <Text variant='heading3'>Delivery & Visibility Options</Text>
        <Box borderTop='0.5px solid' borderColor='colorActionPrimary' width='50%' ml='10px'></Box>
      </Box>

      <Box marginBottom="spaceGapXLarge">
        <Text variant="heading5" marginBottom="spaceGapLarge">
          Email Report Visibility
        </Text>
        <Box display="flex" alignItems="center" margin="12px 0">
          <RadioLabel mb='0' padding='0'>
            <RadioInput
              mr='spaceGapSmall'
              name='visibility_option'
              value="me"
              onChange={(e) => {
                dispatch({ type: 'updateField', payload: e.target })
                dispatch({ type: 'clearError', payload: { name: 'visibilityEmailList' } })
              }}
              checked={state.visibility_option.value === 'me'}
              id="only-me"
            />
          </RadioLabel>
          <Text ml='spaceGapSmall' variant='heading6' as="label" htmlFor="only-me">
            Only Me
          </Text>
        </Box>

        <Box display="flex" alignItems="center" margin="12px 0">
          <RadioLabel mb='0' padding='0'>
            <RadioInput
              mr='spaceGapSmall'
              name="visibility_option"
              value="me_and_team"
              onChange={(e) => dispatch({ type: 'updateField', payload: e.target })}
              checked={state.visibility_option.value === 'me_and_team'}
              id="me-and-my-team"
            />
          </RadioLabel>
          <Text ml='spaceGapSmall' variant='heading6' as="label" htmlFor="me-and-my-team">
            Myself and the following users <Text as='span' variant='body2' fontWeight='normal' color='colorTextDeemphasis'>(separate email addresses by commas)</Text>
          </Text>
        </Box>
        <Textarea
          marginTop="spaceGapMedium"
          id='email-list'
          name='visibilityEmailList'
          height='104px'
          width='100%'
          placeholder='exampleEmail@example.com, email@email.com'
          disabled={state.visibility_option.value !== 'me_and_team'}
          value={state.visibilityEmailList.value}
          onChange={(e) => dispatch({ type: 'updateField', payload: e.target })}
          onBlur={(e) => dispatch({ type: 'updateField', payload: e.target })}
          borderColor={state.visibilityEmailList.value && state.visibilityEmailList.error && 'colorFeedbackError'}
        />
      </Box>

      <Box marginBottom="spaceGapXLarge">
        <Text as="label" variant="heading5" mb="spaceGapMedium" display='block' htmlFor='preferred_delivery_method'>
          Preferred Response Format <RequiredIndicator />
        </Text>
        <DropDownSelect id='preferred_delivery_method' name='preferred_delivery_method' menuLabel="Delivery options" list={deliveryMethodList.map(({ label }) => label)} selectedItem={state.preferred_delivery_method.value} onClick={(item) => dispatch({ type: 'updateField', payload: { name: 'preferred_delivery_method', value: item } })}/>
      </Box>
      <Box borderTop='0.5px solid' borderColor='colorActionPrimary' width='100%'></Box>
      <Box mb="spaceGapXLarge">
        <Box display="flex" alignItems="center" gridGap="8px" my="spaceGapMedium">
          <CheckboxLabel>
            <CheckboxInput
              id="save-data"
              name="save_data"
              checked={state.save_data.value}
              onChange={(e) => dispatch({ type: 'saveToStorage', payload: e.target })}
            />
          </CheckboxLabel>
          <Text variant='heading6' as="label" htmlFor="save-data">
            Save my selections for future investigation requests
          </Text>
        </Box>
        <Text variant="body2" mb="spaceGapXLarge" color="colorTextDeemphasis">
          <RequiredIndicator /> Required fields
        </Text>
      </Box>
    </>
  )
}
